import { Button } from "@landing/components/ui/button"
import { useEffect, useId, useState } from "react"
import { LandingCTA } from "./LandingCTA"
import { LandingTokenRoundel } from "./LandingTokenRoundel"
import { LandinguseBrackyPrice } from "./LandinguseBrackyPrice"
// import { useState } from 'react'
// import { Copy, Check } from 'lucide-react'

export function LandingSectionToken({ userCount = "67K+" }: { userCount?: string } = {}) {
  const { data: brackyData } = LandinguseBrackyPrice()
  const sectionId = useId()
  const [isDesktop, setIsDesktop] = useState(typeof window !== "undefined" ? window.innerWidth >= 640 : true)

  // Update device state on resize
  useEffect(() => {
    if (typeof window === "undefined") return

    const handleResize = () => {
      setIsDesktop(window.innerWidth >= 640)
    }

    window.addEventListener("resize", handleResize)
    return () => window.removeEventListener("resize", handleResize)
  }, [])

  const stripZeros = (s: string) => s.replace(/(\.\d*?[1-9])0+$|\.0+$/, "$1")
  const formatMarketCap = (cap: number) => {
    if (cap >= 1_000_000_000) return `$${stripZeros((cap / 1_000_000_000).toFixed(2))}B`
    if (cap >= 1_000_000) return `$${stripZeros((cap / 1_000_000).toFixed(2))}M`
    if (cap >= 1_000) return `$${stripZeros((cap / 1_000).toFixed(1))}K`
    return `$${stripZeros(cap.toFixed(2))}`
  }

  const tokenDisplay = brackyData?.marketCapUsd ? formatMarketCap(brackyData.marketCapUsd) : "—"

  // const TOKEN_ADDRESS = '0x06f71fb90F84b35302d132322A3C90E4477333b0'
  // const truncatedAddress = `${TOKEN_ADDRESS.slice(0, 6)}...${TOKEN_ADDRESS.slice(-4)}`

  // const [copied, setCopied] = useState(false)
  // const handleCopy = () => {
  //   navigator.clipboard
  //     .writeText(TOKEN_ADDRESS)
  //     .then(() => {
  //       setCopied(true)
  //       setTimeout(() => setCopied(false), 2000)
  //     })
  //     .catch(() => {
  //       /* Clipboard write failed, silently ignore */
  //     })
  // }

  return (
    // Remove horizontal padding on mobile so that the inner wrapper achieves the same 90% viewport width as the CTA
    <section
      id={sectionId}
      className="relative isolate z-0 w-full py-2 sm:py-3 mb-4 mt-4 sm:mb-20 sm:mt-20 flex justify-center px-0 sm:px-4 lg:px-6"
    >
      {/* Width-limited wrapper for token card and CTA */}
      <div className="w-full max-w-[90%] sm:max-w-md lg:max-w-124 xl:max-w-4xl flex flex-col items-center">
        {/* Skewed outer container */}
        {/* Skewed outer container */}
        <div className="relative isolate w-full bg-[#070467] overflow-hidden rounded-3xl px-6 sm:px-8 md:px-10 py-10 md:py-12 transform-gpu xl:transform-[skewX(-16deg)]">
          {/* Animated background video inside blue container */}
          <video
            src="https://a.bracky.app/assets/landing/backgrounds/token-section-background.mp4"
            autoPlay
            muted
            loop
            playsInline
            className="absolute inset-0 w-full h-full object-cover opacity-100 mix-blend-soft-light filter contrast-[2] saturate-0 pointer-events-none -z-20 rounded-3xl transform-gpu xl:transform-[skewX(16deg)_translate(-15%,0%)_scale(1.45)]"
          />
          {/* Scanlines overlay */}
          <div
            className="absolute inset-0 scanlines opacity-30 pointer-events-none -z-10 transform-gpu xl:transform-[skewX(16deg)_translate(0%,-8%)_scale(1.1)]"
            aria-hidden="true"
          />
          {/* Backdrop blur frame around all edges */}
          <div
            className="absolute inset-x-0 top-0 bg-black/10 pointer-events-none -z-5 xl:hidden mask-[linear-gradient(to_bottom,black_0%,black_70%,transparent_100%)]"
            aria-hidden="true"
          />
          {/* Counter-skew wrapper so inner content is upright */}
          <div className="relative transform-gpu xl:transform-[skewX(16deg)]">
            {/* Kicker */}
            <p className="text-[.875rem] sm:text-base xl:ml-8 mt-1 sm:mt-0 font-normal font-mono uppercase [word-spacing:-0.25em] text-white/80 text-center mb-3">
              {/* Mobile copy */}
              <span className="block xl:hidden tracking-wide [word-spacing:-0.3em]">
                JOIN <span className="text-white">{userCount}</span> IN THE BRACKET ARENA
                <br />
              </span>
              {/* Desktop / tablet copy */}
              <span className="hidden xl:inline tracking-wide [word-spacing:-0.25em]">
                LIVE SPORTS' BIGGEST MOMENTS WITH <span className="text-white">{userCount} FANS</span> IN THE BRACKET
                ARENA
              </span>
            </p>

            {/* Card heading */}
            <h2 className="text-white tracking-tight font-extrabold text-center mb-5 xl:mb-4 leading-tight text-h3 xl:ml-6 xl:whitespace-nowrap">
              <span className="whitespace-nowrap">Bet on Bracket with</span>
              <br className="xl:hidden" /> $BRACKY
            </h2>

            <div className="flex flex-col xl:flex-row space-y-1 xl:space-y-0 text-center items-stretch xl:transform xl:translate-x-[-3%]">
              {/* Avatar with rotating text ring */}
              <div className="relative flex-1 pt-6 pb-4 flex flex-col items-center justify-center gap-2 first:after:hidden after:absolute after:left-[20%] after:top-0 after:w-[60%] after:h-px after:bg-[#ffffff]/15 xl:after:hidden xl:before:absolute xl:before:inset-y-0 xl:before:right-0 xl:before:w-px xl:before:bg-[#ffffff]/15 xl:before:transform-[translateZ(0)_skewX(-16deg)] xl:last:before:hidden">
                <span className="scale-[180%] block mt-2 lg:-translate-y-4 xl:translate-y-0 sm:mt-0 lg:mt-8 xl:mt-0">
                  <LandingTokenRoundel
                    inline
                    className="w-22 h-22 mb-[1.7rem] sm:w-24 sm:h-30 mt-[.62rem] sm:mt-3 lg:mt-4 xl:mt-0"
                  />
                </span>
                {/* <span className="text-white/80 text-sm uppercase tracking-wide">$BRACKY</span> */}
              </div>

              {/* Live Market Cap */}
              <div className="relative flex-1 xl:flex-[.88] pt-6 pb-4 flex flex-col items-center justify-center gap-1 first:after:hidden after:absolute after:left-[20%] after:top-0 after:w-[60%] after:h-px after:bg-[#ffffff]/15 xl:after:hidden xl:before:absolute xl:before:inset-y-0 xl:before:right-0 xl:before:w-px xl:before:bg-[#ffffff]/15 xl:before:transform-[translateZ(0)_skewX(-16deg)] xl:last:before:hidden">
                <div className="flex items-center gap-[.4rem]">
                  {/* Live green dot */}
                  <span className="inline-block ml-1.5 w-[0.54rem] h-[0.54rem] rounded-full bg-[#22FF65] shadow-[0_0_3px_#22FF65BD,0_0_8px_#22FF65BD,0_0_15px_#22FF65BD]" />
                  <span className="text-white/80 text-[.875rem] sm:text-base font-normal font-mono uppercase [word-spacing:-0.25em]">
                    LIVE MARKET CAP
                  </span>
                </div>
                <span className="text-white font-extrabold text-h3 mt-[.15rem]">{tokenDisplay}</span>
              </div>

              {/* Buy Button & Base */}
              <div className="relative flex-1 pt-6 pb-4 xl:pl-6 flex flex-col items-center justify-center gap-3 first:after:hidden after:hidden xl:after:absolute xl:after:left-1/4 xl:after:top-0 xl:after:w-1/2 xl:after:h-px xl:after:bg-[#ffffff]/15 xl:before:absolute xl:before:inset-y-0 xl:before:right-0 xl:before:w-px xl:before:bg-[#ffffff]/15 xl:before:transform-[translateZ(0)_skewX(-16deg)] xl:last:before:hidden">
                <Button
                  asChild
                  variant="outline"
                  size="bracket"
                  className="sm:w-[20rem] sm:px-2 md:w-[20rem] md:px-2 lg:w-[20rem] lg:px-2 xl:px-2 xl:w-66 text-white border-white/50 hover:border-white"
                >
                  <a
                    href="https://app.uniswap.org/explore/tokens/base/0x06f71fb90F84b35302d132322A3C90E4477333b0"
                    target="_blank"
                    rel="noopener noreferrer"
                  >
                    Buy $BRACKY
                  </a>
                </Button>
                <div className="flex items-center gap-1 text-white/80 text-sm">
                  {/* Simple white square icon */}
                  <span className="inline-block w-[.9rem] h-[.9rem] mr-1 bg-[#0016f5] rounded-[2px]" />
                  <span>Built on</span>
                  <span className="font-semibold text-white">Base</span>
                </div>
              </div>
            </div>
          </div>
        </div>

        {/* Token CTA - positioned below token card */}
        <div className="flex flex-col items-center mt-8 sm:mt-12 md:mt-16">
          <div className="*:mb-0!">
            <LandingCTA variant="inline" label={isDesktop ? ("Start Trading Now" as string | undefined) : undefined} />
          </div>
          <p className="hidden sm:block mt-4 text-xs sm:text-sm text-white/50 text-center transition-opacity duration-300 ease-out">
            Sign in with X—Start trading in seconds
          </p>
        </div>
      </div>
    </section>
  )
}
